import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// The Android app's public download link — the backend redirects it to the
// current APK in storage, so shipping a new build never needs a site redeploy.
// REACT_APP_APK_URL still overrides it if a different host is ever wanted.
export const APK_DOWNLOAD_URL = process.env.REACT_APP_APK_URL || `${API_BASE}/downloads/android`;

// Without a timeout, a stalled/slow backend request (e.g. a cold-starting
// Render free-tier instance) never resolves nor rejects — anything gating on
// it (like the auth boot check) then hangs forever instead of failing.
const api = axios.create({ baseURL: API_BASE, timeout: 25000 });

api.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem("metazik_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    // localStorage unavailable — proceed without auth header
  }
  return config;
});

// Stable per-browser id so repeated join/heartbeat calls from the same
// viewer count as ONE person in the live viewer count, not one per call.
// Pass the logged-in user's id when available so presence/comments/bans are
// tied to the real account instead of an anonymous per-browser id.
export function getViewerId(authUserId) {
  if (authUserId) return authUserId;
  try {
    let id = localStorage.getItem("metazik_viewer_id");
    if (!id) {
      id = window.crypto?.randomUUID?.() || `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem("metazik_viewer_id", id);
    }
    return id;
  } catch {
    return `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

// Retries a GET a couple of times, a short pause apart, before giving up —
// absorbs a single slow/transient response (a brief mobile-network blip, a
// request that raced a Render restart despite the keep-alive ping) so a
// page's load-error state only shows up for a genuinely persistent
// failure, not a one-off hiccup the user would otherwise have to notice
// and manually retry. Used by pages whose "empty" and "failed to load"
// states are visually distinct (Feed/Store/Library) — see those files.
export async function getWithRetry(url, config, attempts = 3, delayMs = 1500) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await api.get(url, config);
    } catch (e) {
      lastErr = e;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  throw lastErr;
}

// Every uploaded image/avatar/cover is stored as a backend-relative path
// ("/api/files/{id}") so it must be resolved against the API's own origin,
// not the frontend's (they're on different domains in production) — an
// unresolved relative src just 404s against metazikmusic.com itself. An
// already-absolute URL (legacy external covers, data: URIs) is left as-is.
export function resolveImg(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${BACKEND_URL || ""}${u.startsWith("/") ? u : "/" + u}`;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
